'use client';
import React, { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

/* ── Data ────────────────────────────────────────────────── */
const tableRows = [
  {
    metric: 'Initial Response Time',
    statute: { value: '< 2 hours', note: 'Partner direct' },
    typical: { value: '24–72 hours', note: 'Associate triage' },
    statuteWins: true,
  },
  {
    metric: 'Regulatory Fluency',
    statute: { value: 'OCC · FDIC · Fed · CFPB · FinCEN · Basel III', note: 'Practitioner-level' },
    typical: { value: 'OCC · FDIC', note: 'General coverage' },
    statuteWins: true,
  },
  {
    metric: 'Deal-Closing Rate (M&A)',
    statute: { value: '97.3%', note: '147 transactions since 2016' },
    typical: { value: '81%', note: 'Industry average' },
    statuteWins: true,
  },
  {
    metric: 'Partner-Level Involvement',
    statute: { value: 'Every matter', note: 'No handoffs post-engagement' },
    typical: { value: 'Kick-off & close', note: 'Associate-led execution' },
    statuteWins: true,
  },
  {
    metric: 'State Licensing Coverage',
    statute: { value: 'All 50 states + DC', note: 'In-house multi-state team' },
    typical: { value: 'Varies by firm footprint', note: 'Referral-dependent' },
    statuteWins: true,
  },
  {
    metric: 'Consent Order Experience',
    statute: { value: '34 matters resolved', note: 'Zero escalations to enforcement' },
    typical: { value: 'Limited track record', note: 'Generalist approach' },
    statuteWins: true,
  },
];

const testimonials = [
  {
    quote:
      'At 11:47 PM the night before our FDIC exam, I had a partner from Statute on the line walking through our BSA/AML documentation gap. The examiner never saw that gap. That\'s what we pay for.',
    author: 'General Counsel',
    institution: 'Regional Community Bank, Ohio ($4.8B assets)',
    insertAfter: 1,
  },
  {
    quote:
      'We needed licenses in twelve states to launch our payments product. Statute closed all twelve in ninety days. Our prior counsel said it would take eighteen months.',
    author: 'Chief Financial Officer',
    institution: 'Fintech Payments Startup, Series B',
    insertAfter: 3,
  },
  {
    quote:
      'The merger opinion Statute delivered was the cleanest document I\'ve seen in thirty years. The Fed\'s examiners had no follow-up questions. We closed on schedule.',
    author: 'Managing Director',
    institution: 'Private Equity Sponsor, $2.1B Fund',
    insertAfter: 5,
  },
];

function TestimonialTile({ testimonial }: { testimonial: typeof testimonials[0] }) {
  return (
    <tr>
      <td colSpan={3} className="py-0">
        <div
          className="testimonial-tile mx-0 my-3 p-7 rounded-sm"
          style={{
            background: 'linear-gradient(135deg, #0E2347 0%, #0B1D3A 100%)',
            borderLeft: '3px solid #B8943E',
          }}
        >
          <p
            className="font-serif italic text-marble/85 leading-relaxed mb-4"
            style={{ fontSize: 'clamp(1rem, 1.5vw, 1.15rem)' }}
          >
            &ldquo;{testimonial.quote}&rdquo;
          </p>
          <div className="flex items-center gap-3">
            <div
              className="w-6 h-px"
              style={{ background: '#B8943E' }}
            />
            <div>
              <p className="text-[11px] font-semibold tracking-widest uppercase text-gold">
                {testimonial.author}
              </p>
              <p className="text-[11px] text-marble/40 tracking-wide">
                {testimonial.institution}
              </p>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function ComparisonMosaicSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionRef.current?.querySelectorAll('.reveal-up').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  /* Interleave testimonials into rows */
  const buildRows = () => {
    const items: React.ReactNode[] = [];
    tableRows.forEach((row, idx) => {
      items.push(
        <tr
          key={`row-${idx}`}
          className="group transition-colors"
          style={{ borderBottom: '1px solid rgba(184,148,62,0.1)' }}
        >
          {/* Metric */}
          <td className="py-5 pr-6 align-top">
            <span className="text-[13px] font-semibold text-navy tracking-wide">
              {row.metric}
            </span>
          </td>

          {/* Statute */}
          <td className="py-5 px-6 align-top" style={{ background: 'rgba(184,148,62,0.04)' }}>
            <div className="flex items-start gap-2">
              <Icon name="CheckCircleIcon" variant="solid" size={16} className="check-gold mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[13px] font-semibold text-navy">{row.statute.value}</p>
                <p className="text-[11px] text-navy/50 mt-0.5">{row.statute.note}</p>
              </div>
            </div>
          </td>

          {/* Typical */}
          <td className="py-5 pl-6 align-top">
            <div className="flex items-start gap-2">
              <Icon name="MinusCircleIcon" variant="solid" size={16} className="text-navy/20 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[13px] text-navy/60">{row.typical.value}</p>
                <p className="text-[11px] text-navy/35 mt-0.5">{row.typical.note}</p>
              </div>
            </div>
          </td>
        </tr>
      );

      const testimonial = testimonials.find((t) => t.insertAfter === idx + 1);
      if (testimonial) {
        items.push(<TestimonialTile key={`testimonial-${idx}`} testimonial={testimonial} />);
      }
    });
    return items;
  };

  return (
    <section
      id="comparison"
      ref={sectionRef}
      className="py-24 px-6"
      style={{ background: '#F4F3EF' }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="reveal-up mb-4 text-center">
          <span className="text-[10px] font-semibold tracking-widest3 uppercase text-gold">
            The Statute Standard
          </span>
        </div>
        <div className="reveal-up mb-3 text-center">
          <h2
            className="font-serif font-light text-navy"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', letterSpacing: '-0.02em' }}
          >
            Why General Counsel Switch.
          </h2>
        </div>
        <div className="reveal-up mb-14 text-center">
          <p className="text-[13px] text-navy/50 max-w-xl mx-auto leading-relaxed">
            A direct comparison between Statute and typical outside banking counsel — measured by the metrics that matter when the examiner is in the building.
          </p>
        </div>

        <div className="gold-rule mb-10" />

        {/* Table */}
        <div className="reveal-up overflow-x-auto">
          <table
            className="comparison-table w-full"
            style={{ borderCollapse: 'collapse' }}
          >
            <thead>
              <tr>
                <th
                  className="pb-4 pr-6 text-left text-[10px] font-semibold tracking-widest2 uppercase text-navy/40 w-1/3"
                  style={{ borderBottom: '2px solid #B8943E' }}
                >
                  Evaluation Criteria
                </th>
                <th
                  className="pb-4 px-6 text-left text-[10px] font-semibold tracking-widest2 uppercase w-1/3"
                  style={{ borderBottom: '2px solid #B8943E', color: '#B8943E' }}
                >
                  Statute LLP
                </th>
                <th
                  className="pb-4 pl-6 text-left text-[10px] font-semibold tracking-widest2 uppercase text-navy/30 w-1/3"
                  style={{ borderBottom: '2px solid rgba(184,148,62,0.2)' }}
                >
                  Typical Outside Counsel
                </th>
              </tr>
            </thead>
            <tbody>{buildRows()}</tbody>
          </table>
        </div>

        <div className="gold-rule mt-10" />
      </div>
    </section>
  );
}